import { mergeProps } from '@base-ui/react/merge-props';
import { useRender } from '@base-ui/react/use-render';
import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';

import { cn } from '../lib/cn';
import { Icon } from './icon';

export interface ListItemButtonProps extends useRender.ComponentProps<'button'> {
  /** 앞 아이콘 하나. */
  icon?: LucideIcon;
  /** 제목 아래 보조 한 줄. */
  description?: ReactNode;
  /** 오른쪽 끝 메타(날짜 · 개수 · 상태). 누를 것을 두지 않는다 — 행 전체가 클릭 대상 하나다. */
  meta?: ReactNode;
}

/**
 * 패널 안 테두리 없는 한 줄 동작 — 행 전체가 클릭 대상 하나다(D147 ③).
 * 테두리 있는 행은 `ListRow`, 배타 선택은 `SelectableListItem`이다.
 * 하루 수십 번 누르는 큰 면이라 눌림 scale이 없고 hover 색만 바뀐다(D4-20).
 * 링크면 `render={<Link … />}`로 넘긴다.
 */
export function ListItemButton({
  icon,
  description,
  meta,
  render,
  className,
  children,
  ...props
}: ListItemButtonProps) {
  return useRender({
    defaultTagName: 'button',
    render,
    props: mergeProps<'button'>(
      {
        ...(render ? {} : { type: 'button' as const }),
        className: cn(
          'gap-row px-row py-snug flex w-full items-center rounded-lg text-left',
          'hover:bg-highlight ease-flow duration-fast transition-colors',
          'disabled:cursor-default disabled:opacity-60 disabled:hover:bg-transparent',
          className,
        ),
        children: (
          <>
            {icon ? <Icon as={icon} size="sm" className="text-ink-muted" /> : null}
            <span className="flex min-w-0 flex-1 flex-col">
              <span className="text-ink truncate text-sm">{children}</span>
              {description !== undefined ? (
                <span className="text-ink-muted truncate text-xs">{description}</span>
              ) : null}
            </span>
            {meta !== undefined ? (
              <span className="text-ink-muted shrink-0 text-xs">{meta}</span>
            ) : null}
          </>
        ),
      },
      props,
    ),
  });
}
